import { debounce, isEmpty } from 'lodash';
import {
  AsyncCreatableMultiselect,
  MultiselectInstance,
  SelectOptionsOrGroups,
} from '@ardoq/select';
import { columnPredicates, FilterValue, ReportColumn } from '@ardoq/api-types';
import {
  ColumnFilterMultiselectWithLoadSuggestionsProps,
  ReportColumnSuggestionsFetchFn,
} from '../types';
import { isNonEmptyColumn } from '../utils';
import {
  getAnyValueFilterLabel,
  getNoValueFilterLabel,
  HAS_VALUE_OR_REFERENCE_VALUE,
} from './filterUtils';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ColumnFilterLabel,
  RemoveFilterButtonInTopRightCorner,
  SelectContainer,
} from './atoms';
import { Maybe } from '@ardoq/common-helpers';
import type { ColumnFilterMultiselectLabels } from '../labels';

const DEFAULT_LABELS = {
  searchPlaceholder: 'Search...',
  defaultOptionsGroupLabel: 'Default options',
  suggestionsGroupLabel: 'Suggestions',
  searchByTemplate: (inputValue: string) => `Search by "${inputValue}"`,
} satisfies Required<ColumnFilterMultiselectLabels>;

const DEFAULT_REMOVE_FILTER_BUTTON_LABEL = 'Remove filter';

const getLoadOptionsFn = (
  loadOptionsFunction: ReportColumnSuggestionsFetchFn,
  column: ReportColumn,
  labels: Required<ColumnFilterMultiselectLabels>
) => {
  return async (
    inputValue: string
  ): Promise<SelectOptionsOrGroups<FilterValue>> => {
    const suggestions = await loadOptionsFunction(column.key, inputValue);

    const unsetOrNaOption = {
      label: getNoValueFilterLabel(column),
      value: null,
    };
    const hasValueOption = {
      label: getAnyValueFilterLabel(column),
      value: HAS_VALUE_OR_REFERENCE_VALUE,
    };

    return [
      ...(isNonEmptyColumn(column) ||
      columnPredicates.isTextColumn(column) ||
      columnPredicates.isEmailColumn(column)
        ? []
        : [
            {
              label: labels.defaultOptionsGroupLabel,
              options: [hasValueOption, unsetOrNaOption],
            },
          ]),
      { label: labels.suggestionsGroupLabel, options: suggestions },
    ];
  };
};

const ColumnFilterMultiselectWithLoadSuggestions = ({
  column,
  selected,
  onValueChange,
  loadSuggestions,
  removeFilter,
  menuTarget,
  allowFreeText = false,
  showLabel = true,
  isReadOnly,
  withCloseButton = true,
  labels,
  removeFilterButtonLabel = DEFAULT_REMOVE_FILTER_BUTTON_LABEL,
}: ColumnFilterMultiselectWithLoadSuggestionsProps & {
  menuTarget: Maybe<HTMLElement>;
  allowFreeText?: boolean;
  showLabel?: boolean;
  withCloseButton?: boolean;
  labels?: ColumnFilterMultiselectLabels;
  removeFilterButtonLabel?: string;
}) => {
  const mergedLabels = { ...DEFAULT_LABELS, ...labels };
  const ref = useRef<MultiselectInstance<FilterValue> | null>(null);
  const [isDefaultOptionsFetched, setIsDefaultOptionsFetched] = useState<
    undefined | true
  >(undefined);

  const onFocus = () => {
    if (isDefaultOptionsFetched) return; // should not happen more than once after initial focus
    setIsDefaultOptionsFetched(true);
  };

  useEffect(() => {
    if (isDefaultOptionsFetched) {
      ref.current?.focus();
    }
  }, [isDefaultOptionsFetched]);

  const Wrapper = useMemo(
    () =>
      showLabel
        ? SelectContainer
        : ({ children }: React.PropsWithChildren) => children,
    [showLabel]
  );

  return (
    <Wrapper>
      {!isReadOnly && withCloseButton && (
        <RemoveFilterButtonInTopRightCorner
          onClick={() => removeFilter(column.key)}
          label={removeFilterButtonLabel}
        />
      )}
      <AsyncCreatableMultiselect<FilterValue>
        // @ts-expect-error TODO fix types on Multiselect, should be string | ReactNode
        label={
          showLabel ? (
            <ColumnFilterLabel
              column={column}
              removeFilterLabel={removeFilterButtonLabel}
            />
          ) : undefined
        }
        placeholder={mergedLabels.searchPlaceholder}
        loadOptions={debounce(
          getLoadOptionsFn(loadSuggestions, column, mergedLabels),
          100,
          {
            leading: true,
          }
        )}
        onFocus={onFocus}
        defaultOptions={isDefaultOptionsFetched}
        openMenuOnFocus={true}
        value={selected}
        onChange={values => onValueChange(values ?? [])}
        dataTestId={'report-sidebar-component-filter-multiselect'}
        key={String(isDefaultOptionsFetched)} // internal key, only needed to rerender select so defaultOptions update is reflected
        selectRef={ref}
        menuPortalTarget={menuTarget}
        enableMenuPlacementFix
        createOptionPosition="first"
        isValidNewOption={(inputValue: string) =>
          allowFreeText && !isEmpty(inputValue)
        }
        formatCreateLabel={(inputValue: string) =>
          mergedLabels.searchByTemplate(inputValue)
        }
        isReadOnly={isReadOnly}
        isClearable={!isReadOnly}
      />
    </Wrapper>
  );
};

export { ColumnFilterMultiselectWithLoadSuggestions };
